import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';

import {
  Select as BaseSelect,
  InputLabel,
  FormHelperText,
  MenuItem,
} from '@material-ui/core';
import { useField } from '@unform/core';

import * as S from './styles';

function getOptionsCollectionArray(options) {
  const arr = [];
  for (let i = 0; i < options.length; i += 1) {
    arr.push(options[i]);
  }
  return arr;
}

function isValidValue(value) {
  return typeof value !== 'undefined' && value !== '';
}

function Select({
  name,
  label,
  style,
  className,
  defaultValue,
  native,
  onChange,
  getValue,
  data = [],
  value: valueProp,
  multiple,
  variant = 'outlined',
  minWidth = 200,
  ...restProps
}) {
  const {
    fieldName,
    registerField,
    defaultValue: defaultFieldValue,
    error,
  } = useField(name);

  const inputRef = useRef(null);
  const defaultInputValue = useMemo(() => {
    if (multiple) {
      return defaultFieldValue || defaultValue || [];
    }
    return defaultFieldValue || defaultValue || '';
  }, [defaultFieldValue, defaultValue, multiple]);
  const [inputValue, setInputValue] = useState(defaultInputValue);

  const _handleChange = useCallback(
    (e) => {
      const el = e.target;
      let value;

      if (native && multiple) {
        value = getOptionsCollectionArray(el.options)
          .filter((opt) => opt.selected)
          .map((opt) => opt.value);
      } else {
        value = el.value;
        !!getValue && getValue(data.find((d) => d.value === el.value));
      }

      if (valueProp === undefined && onChange === undefined) {
        setInputValue(() => value);
      }

      if (valueProp === undefined && typeof onChange === 'function') {
        setInputValue(() => value);
        onChange(e, null);
      }

      if (valueProp !== undefined && typeof onChange === 'function') {
        onChange(e, null);
      }
    },
    [valueProp, onChange, setInputValue, multiple, native, data]
  );

  useEffect(() => {
    if (fieldName) {
      registerField({
        name: fieldName,
        ref: inputRef.current,
        getValue() {
          return valueProp || inputValue;
        },
        setValue(_, newValue) {
          _handleChange({
            target: { value: newValue },
          });
        },
      });
    }
  }, [fieldName, registerField, _handleChange, native, valueProp, inputValue]);

  const baseSelectProps = useMemo(
    () => ({
      value: !valueProp ? inputValue : valueProp,
      inputProps: {
        ...restProps.inputProps,
        ref: inputRef,
      },
      defaultValue: defaultInputValue || inputValue,
      onChange: _handleChange,
      name,
      multiple,
      label,
      ...restProps,
    }),
    [
      inputValue,
      defaultInputValue,
      name,
      restProps,
      _handleChange,
      valueProp,
      multiple,
      label,
    ]
  );

  const shrink = useMemo(() => {
    if (native) {
      return true;
    }

    if (multiple) {
      return !!(valueProp || inputValue).length;
    }

    return isValidValue(valueProp) || !!isValidValue(inputValue);
  }, [native, multiple, inputValue, valueProp]);

  return (
    <S.Wrapper
      style={{ minWidth, ...style }}
      className={className}
      error={!!error}
      variant={variant}
      size="small"
    >
      {!!label && (
        <InputLabel shrink={shrink} {...{ 'data-testid': 'select-label' }}>
          {label}
        </InputLabel>
      )}

      <BaseSelect {...baseSelectProps} native={native}>
        {data.map((option, index) => (
          <MenuItem value={option.value} key={index}>
            {option.label}
          </MenuItem>
        ))}
      </BaseSelect>

      {!!error && <FormHelperText>{error}</FormHelperText>}
    </S.Wrapper>
  );
}

export default React.memo(Select);
